/***********************************************************************************************
 * 사투리 영상 목록 컴포넌트
 *
 * Copyright (c) 2023 박주형
 *
 * 주의: 사투리 영상만 표시하는 컴포넌트
 **********************************************************************************************/
import {AfterViewInit, Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {Subscription} from "rxjs";
import {finalize} from "rxjs/operators";

import {CITY2, isCity2Type} from "../../shared/defines/gukeo.const";
import {VIDEO_CATEGORY} from "../../shared/defines/protocol";
import {SearchParam} from 'src/app/shared/defines/client.const';
import {Video} from "../../shared/models/video.model";
import {VideoLoader} from "../../shared/interfaces/VideoLoader";
import {VideoService} from 'src/app/shared/services/video/video.service';
import {GetCity2DisplayTxt} from "../../shared/defines/desktop.const";
import {RedPipeService} from "../../shared/services/red-pipe/red-pipe.service";
import {LayoutMgrService} from "../../shared/services/layout-mgr/layout-mgr.service";


@Component({
  selector: 'app-movie-list',
  templateUrl: './movie-list.component.html',
  styleUrls: ['./movie-list.component.scss']
})
export class MovieListComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'menu-group-box flex-grow';
  @HostBinding('style.display') styleBlock = 'block';
  @ViewChild('extContainer') extContainer?: ElementRef;
  @ViewChild('extHeader') extHeader?: ElementRef;
  @ViewChild('extBody') extBody?: ElementRef;

  @Input() story_no: number | null = null;
  @Input() city2: CITY2 | null = null;

  protected readonly GetCity2DisplayTxt = GetCity2DisplayTxt;


  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  private resizeObserver?: ResizeObserver;

  public vl: VideoLoader | null = null;

  public listVideo: Array<Video> | null = null;
  private load$: Subscription | null = null;
  public get IsLoading(): boolean {
    // console.log('IsLoading()', this.subscription, this.subscription !== null);
    return this.load$ !== null;
  }

  private queryParams$?: Subscription;

  private sendingBookmark = false;

  // redPipe 처리
  private redPipe$: Subscription | null = null;


  constructor(
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private lm: LayoutMgrService,
    private videoSvc: VideoService,
    private redPipeSvc: RedPipeService,
  ) {

    /******************************************************************************************
     * 주소 뒤의 파라미터를 받아오는 곳.
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('MovieListComponent.queryParams', params);

        this.queryParams = {
          ...params,
          cat: VIDEO_CATEGORY.DIALECT,
        };
        // const regionName = params['region'] ?? null;
        // if (regionName) {
        //   const region = regionName ? GetRegionByName(regionName) : null;
        //   if (region === null) {
        //     console.error('MovieListComponent.queryParams invalid region = ' + regionName);
        //   } else {
        //     // this.region = region || REGION.ALL;
        //     this.queryParams = {...this.queryParams, region: regionName};
        //   }
        // }
        //
        // const story = params['story'] ?? null;
        // if (story) {
        //   this.queryParams = {...this.queryParams, story};
        // }

        // console.warn('MovieListComponent.queryParams', this.queryParams);

        this.vl = this.videoSvc;
        this.ReloadList(this.queryParams);
      });

    this.redPipe$ = this.redPipeSvc.Click$.subscribe((red) => {
      this.extBody?.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
        // behavior: 'auto',
      });
    });
  }

  ngOnInit() {
    // console.warn('MovieSearchComponent::ngOnInit() story_no =', this.story_no, 'region =', this.region);
    let param: SearchParam = {
      cat: VIDEO_CATEGORY.DIALECT,
    };
    if (this.story_no !== null) {
      param = {...param, story: this.story_no};
    }
    else if (this.city2 !== null) {
      // console.warn('MovieSearchComponent::ngOnInit() region =', this.region);

      if (!isCity2Type(this.city2)) {
        throw new Error('MovieSearchComponent::ngOnInit() invalid city = ' + this.city2);
      }
      param = {...param, city: this.city2};
    }

    this.ReloadList(param);
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        console.log('Component size changed:', entry.contentRect);

        if (this.extContainer && this.extBody) {
          const containerHeight = this.extContainer.nativeElement.offsetHeight;
          const headerHeight = this.extHeader?.nativeElement.offsetHeight ?? 0;
          this.extBody.nativeElement.style.setProperty('max-height', (containerHeight - headerHeight) + 'px');
        }
      }
    });

    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy() {
    // YouTube 플레이어 팝업이 열려있으면 닫기
    this.lm.CloseYoutubePopup();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.queryParams$?.unsubscribe();

    this.redPipe$?.unsubscribe();
    this.redPipe$ = null;

    // 추가: subscription 구독 해지
    this.load$?.unsubscribe();
    this.load$ = null;
  }

  public ReloadList(param?: SearchParam): void {
    // console.log('MovieSearchComponent::ReloadList() param =', param);
    this.listVideo = null;

    if (this.vl === null) {
      throw new Error('MovieSearchComponent::ReloadList() vl is null');
    }
    this.load$ = this.vl.GetList(param)
      .pipe(
        finalize(() => this.load$ = null)
      ).subscribe((listVideo: Array<Video>) => {
        // console.log('MovieSearchComponent::ReloadList() listVideo =', listVideo);
        this.listVideo = listVideo;
      }, (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.load$ = null;
          console.log('MovieSearchComponent::ReloadList() 404', this.load$, this.IsLoading);
        } else {
          console.error(err);
        }
      });
  }
}
